import { css } from '@emotion/css';
import { BottomNavigation, BottomNavigationAction, Paper, useTheme } from '@mui/material';
import React from 'react';

import { MOBILE_BOTTOM_NAV_HEIGHT } from '../layout/chromeSizes';

export interface BottomNavItem {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface MobileBottomNavProps {
  items: BottomNavItem[];
  activeValue: string;
  onChange: (value: string) => void;
}

/**
 * Mobile bottom navigation component
 * Provides quick access to primary navigation items on mobile devices
 */
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ items, activeValue, onChange }) => {
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === 'dark';

  const styles = {
    container: css({
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: theme.zIndex.appBar,
      borderTop: `1px solid ${theme.palette.divider}`,
      paddingBottom: 'env(safe-area-inset-bottom)',
    }),
    navigation: css({
      backgroundColor: isDarkTheme ? theme.palette.background.paper : '#ffffff',
      height: MOBILE_BOTTOM_NAV_HEIGHT,
      '& .MuiBottomNavigationAction-root': {
        minWidth: 'auto',
        padding: theme.spacing(1, 0.5),
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
          color: theme.palette.primary.main,
        },
      },
      '& .MuiBottomNavigationAction-label': {
        fontSize: '0.625rem',
        marginTop: theme.spacing(0.5),
        '&.Mui-selected': {
          fontSize: '0.625rem',
        },
      },
    }),
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    onChange(newValue);
  };

  return (
    <Paper className={styles.container} elevation={8} data-testid="mobile-bottom-nav">
      <BottomNavigation
        value={activeValue}
        onChange={handleChange}
        showLabels
        className={styles.navigation}
      >
        {items.map((item) => (
          <BottomNavigationAction
            key={item.value}
            value={item.value}
            label={item.label}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
